import { derived, get, writable } from 'svelte/store';
import { format } from 'date-fns';

import { VirtualBackgroundService } from '@client/services/virtual-background';

import {
  colorBackground,
  colorPrimary,
  colorSecondary,
  colorTertiary,
  colorText,
  companyName,
  companyUrl,
  fontBase,
  fontHeadings,
  fontsUrl,
  fontWeightBase,
  fontWeightHeadings,
  iconUrl,
  logoUrl
} from '@client/stores/brand';
import { userStore } from '@client/stores/user';

import { throttle } from '@common/utils/throttle';

import { loadBackgroundTemplate } from './virtual-background-templates';

/**
 * @typedef {import('./types').AssignedField} AssignedField
 * @typedef {import('./types').Brand} Brand
 * @typedef {import('./types').VirtualBackground} VirtualBackground
 */

const BACKGROUND_API = import.meta.env.VITE_BACKGROUND_API;

/** @type {VirtualBackground} */
const BLANK_TEMPLATE = {
  background: '#000',
  foreground: '#FFF',
  cells: [[], [], [], [], [], [], [], [], []],
  blur: 0,
  brightness: 1
};

// Template-related stores

/**
 * The unique identifier (rulename) of the virtual background.
 * @type {import('svelte/store').Writable<string>}
 */
export const id = writable('');

/**
 * Tracks updates made to the rulename.
 * @type {import('svelte/store').Writable<string>}
 */
export const updatedRulename = writable('');

/**
 * A store that holds true if the current template is valid, otherwise false.
 * @type {import('svelte/store').Writable<boolean>}
 */
export const isValid = writable(true);

/**
 * A store that holds true while a background or template is being loaded.
 * @type {import('svelte/store').Writable<boolean>}
 */
export const loading = writable(false);

/**
 * A store that holds true while a background is being saved or updated.
 * @type {import('svelte/store').Writable<boolean>}
 */
export const saving = writable(false);

/**
 * A store containing the raw template for the virtual background.
 * @type {import('svelte/store').Writable<VirtualBackground>}
 */
export const template = writable();

const generateUniqueRulename = (name) => {
  return `${name}_${format(new Date(), 'MMddyy-HHmm')}`;
};

// Derived Stores

const CELL_PARAMS = ['TL', 'T', 'TR', 'L', 'C', 'R', 'BL', 'B', 'BR'];
const FIELD_PATTERN = /\{\{\s*(.*?)\s*\}\}/g;
const INTERNAL_VARIABLES = new Set(['image_url', 'smart_url', 'undefined']);

/**
 * Extracts field names from a raw cell string.
 * @param {string} cell
 * @param {Set<string>} set
 */
const extractFields = (cell, set) => {
  for (const match of cell.matchAll(FIELD_PATTERN)) {
    const fieldName = match[1].split('|')[0].trim();

    if (fieldName && !fieldName.startsWith('"') && !fieldName.startsWith('sg') && !INTERNAL_VARIABLES.has(fieldName)) {
      set.add(fieldName);
    }
  }
};

/**
 * A derived store that automatically extracts all template fields from the `template` store.
 * Filters out internal variables.
 * @type {import('svelte/store').Readable<string[]>}
 */
export const fields = derived(template, ($template) => {
  if (!$template?.cells) return [];

  try {
    /** @type {Set<string>} */
    const variables = new Set();

    // Flatten the 2D array and process
    $template.cells.flat().forEach((cell) => extractFields(cell, variables));

    return Array.from(variables).sort();
  } catch (e) {
    console.error('Failed to parse template fields:', e);
    return [];
  }
});

// Preview-related writable stores

/**
 * The background type for generating previews.
 * @type {import('svelte/store').Writable<string>}
 */
export const previewType = writable('zoom');

/**
 * A store holding an map of original field names to their new values, remapped variable names, and default values.
 * @type {import('svelte/store').Writable<Record<string, AssignedField>>}
 */
export const reassignedFields = writable({});

/**
 * A store containing the rendered preview of virtual background cells.
 *
 * @type {import('svelte/store').Writable<VirtualBackground>}
 */
export const renderedTemplate = writable(BLANK_TEMPLATE);

/**
 * A derived store that automatically generates background image URL from template and user data.
 * @type {import('svelte/store').Readable<string>}
 */
export const url = derived([renderedTemplate, previewType], ([$template, $previewType]) => {
  if (!$template) return '';

  const params = new URLSearchParams();

  $template.cells.forEach((cell, index) => {
    if (cell && cell.length > 0) {
      const paramKey = CELL_PARAMS[index];

      cell.forEach((line) => {
        params.append(paramKey, line);
      });
    }
  });

  if ($template.background) {
    params.set('BG', $template.background);
  }
  if ($template.foreground) {
    params.set('FG', $template.foreground);
  }

  if ($template.blur) {
    params.set('BLUR', $template.blur);
  }

  if ($template.brightness < 1) {
    params.set('DARK', $template.brightness);
  }

  if ($previewType === 'teams') {
    params.append('P', '100');
    params.append('P', '50');
  }

  return `${BACKGROUND_API}/background?${params.toString()}`;
});

/**
 * The user object used for generating previews.
 * @type {import('svelte/store').Writable<any>}
 */
export const selectedUser = writable(null);

// Actions

/**
 * Resets all stores to their initial, empty state.
 * @returns {void}
 */
export const resetStores = () => {
  id.set('');
  isValid.set(true);
  template.set(BLANK_TEMPLATE);

  reassignedFields.set({});
  renderedTemplate.set(BLANK_TEMPLATE);
};

/**
 * Loads an existing background from the VirtualBackgroundService and populates the stores.
 * @param {string} backgroundId - The ID (rulename) of the background to load.
 * @returns {Promise<void>}
 */
export async function loadBackground(backgroundId) {
  loading.set(true);
  resetStores();
  id.set('');

  try {
    const response = await VirtualBackgroundService.getById(backgroundId);
    id.set(response.rulename);
    template.set(response.template);
  } catch (e) {
    console.error(e);
  } finally {
    loading.set(false);
  }
}

/**
 * Gathers current brand settings from various stores into a single Brand object.
 * @returns {Brand} The consolidated brand object.
 */
export const getTemplateDefaults = () => {
  const currentUser = get(userStore);

  const brandDefaults = {
    colorBackground: get(colorBackground) || '#ffffff',
    colorPrimary: get(colorPrimary) || '#2563eb',
    colorSecondary: get(colorSecondary) || '#9ec3ff',
    colorTertiary: get(colorTertiary) || '#a855f7',
    colorText: get(colorText) || '#1f2937',
    companyName: get(companyName),
    companyUrl: get(companyUrl) || currentUser.domain,
    fontBase: get(fontBase) || 'sans-serif',
    fontHeadings: get(fontHeadings) || 'sans-serif',
    fontWeightBase: get(fontWeightBase) || '400',
    fontWeightHeadings: get(fontWeightHeadings) || '700',
    iconUrl: get(iconUrl),
    logoUrl:
      get(logoUrl) ||
      'data:image/svg+xml,%3Csvg%20viewBox%3D%220%200%20160%2040%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Crect%20x%3D%221%22%20y%3D%221%22%20width%3D%22158%22%20height%3D%2238%22%20fill%3D%22black%22%20fill-opacity%3D%22.2%22%20stroke%3D%22white%22%20stroke-dasharray%3D%224%22%20%2F%3E%3Ctext%20x%3D%227%22%20y%3D%2225%22%20fill%3D%22white%22%20font-family%3D%22sans-serif%22%3EYOUR%20LOGO%20HERE%3C%2Ftext%3E%3C%2Fsvg%3E',
    fontsUrl: get(fontsUrl)
  };

  return brandDefaults;
};

/**
 * Loads a pre-defined template by name and populates the stores.
 * @param {string} name - The name of the template to load.
 * @returns {Promise<void>}
 */
export async function loadTemplate(name) {
  loading.set(true);
  resetStores();

  try {
    const brandDefaults = getTemplateDefaults();

    const tpl = loadBackgroundTemplate(name, brandDefaults);

    id.set(generateUniqueRulename(name.toLowerCase()));

    template.set(tpl);
  } catch (e) {
    console.error(e);
  } finally {
    loading.set(false);
  }
}

/**
 * Updates the mapping for a single field.
 * @param {AssignedField} field - The field to reassign.
 * @returns {void}
 */
export function reassignField(field) {
  reassignedFields.update((store) => {
    return { ...store, [field.name]: field };
  });
}

/**
 * Resets the mapping for a single field.
 * @param {string} fieldName - The field name to reassign.
 * @returns {void}
 */
export function resetField(fieldName) {
  reassignedFields.update((store) => {
    const { [fieldName]: _, ...rest } = store;
    return rest;
  });
}

/**
 * Creates a new background by persisting the updated template and settings.
 * @returns {Promise<boolean>} A promise that resolves to true on success, false on failure.
 */
export async function saveBackground() {
  saving.set(true);

  let tpl = get(template);

  try {
    const params = get(fields).map((name) => ({
      name,
      default: '',
      hide: false,
      transform: false
    }));
    const rulename = get(updatedRulename);
    const newBackground = {
      template: tpl,
      params,
      rulename,
      status: false
    };

    await VirtualBackgroundService.update(newBackground);

    return true;
  } catch (e) {
    console.error(e);

    return false;
  } finally {
    saving.set(false);
  }
}

/**
 * Sets the selected user for preview rendering.
 * @param {any} user - The user object for the preview.
 * @returns {void}
 */
export function setSelectedUser(user) {
  selectedUser.set(user);
}

/**
 * Updates an existing background with the latest template changes.
 * @returns {Promise<boolean>} A promise that resolves to true on success, false on failure.
 */
export async function updateBackground() {
  saving.set(true);

  let tpl = get(template);

  try {
    const currentId = get(id);
    const background = await VirtualBackgroundService.getById(currentId);
    const rulename = get(updatedRulename);

    await VirtualBackgroundService.update({
      ...background,
      rulename,
      template: tpl
    });

    return true;
  } catch (e) {
    console.error(e);

    return false;
  } finally {
    saving.set(false);
  }
}

/**
 * Renders the preview by applying all user changes (remapped fields, preview user)
 * @returns {Promise<void>}
 */
export async function updatePreview() {
  const originalTemplate = get(template);

  if (!originalTemplate) {
    renderedTemplate.set(BLANK_TEMPLATE);
    return;
  }

  // Create preview data from selected user and domain
  const currentUser = get(userStore);
  const [sgcnameproto, sgcname] = currentUser.cname;
  const previewUser = get(selectedUser) || {};

  /** @type {Record<string, string>} */
  const previewData = {
    ...previewUser,
    sgcnameproto,
    sgcname,
    sgsusername: previewUser.username || ''
  };

  /**
   * Replace {{field}} or {{field|default}} placeholders
   * @param {string} content
   * @returns {string}
   */
  const replaceFields = (content) => {
    if (!content) return content;

    return content
      .replace(/\{\{([\s\S]+?\|[\s\S]+?)\}\}/g, (match, innerContent) => {
        // Images: pass through as-is (parseImage will extract the URL at render time)
        if (innerContent.includes('| img')) {
          return match;
        }

        // QR codes: perform replacement only in the qr data string
        if (innerContent.includes('| qr_code')) {
          return match.replace(/\{\{(.+?)\}\}/g, (nestedMatch, varName) => {
            const key = varName.trim();
            return previewData[key] !== undefined ? previewData[key] : nestedMatch;
          });
        }

        return match;
      })
      .replace(
        /\{\{\s*(\w+)\s*(?:\|\s*default:\s*(?:"([^"]*)"|(\w+)))?\s*\}\}/g,
        (match, key, literalDefault, fieldDefault) => {
          if (previewData[key] !== undefined) return previewData[key];
          if (literalDefault !== undefined) return literalDefault;
          if (fieldDefault !== undefined) return previewData[fieldDefault] ?? '';
          return '';
        }
      );
  };

  // Process all cells
  const processedCells = originalTemplate.cells.map((cell) => cell.map(replaceFields));

  renderedTemplate.set({
    background: originalTemplate.background,
    foreground: originalTemplate.foreground,
    cells: processedCells,
    blur: originalTemplate.blur,
    brightness: originalTemplate.brightness
  });
}

/**
 * Directly updates the raw template store with new content.
 *
 * @param {VirtualBackground} newTemplate - The new template.
 * @returns {void}
 */
export function updateTemplate(newTemplate) {
  template.set(newTemplate);
}

// Subscriptions to update preview automatically
const throttledUpdatePreview = throttle(updatePreview, 75);

reassignedFields.subscribe(throttledUpdatePreview);
selectedUser.subscribe(throttledUpdatePreview);
template.subscribe(throttledUpdatePreview);
